// ABOUTME: Lightweight dialog to select invoice view type (price_qty / component)
// ABOUTME: Used when generating an invoice for an SO that has no sub-service orders.

import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, CircularProgress, Alert,
    RadioGroup, Radio, FormControl, FormLabel, FormControlLabel,
    Chip, TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import invoicingService from '../../services/invoicing';

const InvoiceViewPreferencesDialog = ({ open, onClose, onConfirm, companyTier }) => {
    const [viewType, setViewType] = useState('price_qty');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        Promise.all([
            invoicingService.getSettings(),
        ]).then(([settingsResp]) => {
            const systemDefault = settingsResp.data?.invoice_view_type || 'price_qty';
            setViewType(systemDefault === 'service_type' ? 'price_qty' : systemDefault);
        }).catch(() => {
            setError('Failed to load invoice preferences data');
        }).finally(() => setLoading(false));
    }, [open]);

    const handleClose = () => {
        setViewType('price_qty');
        setError(null);
        onClose();
    };

    const handleConfirm = () => {
        if (viewType === 'price_qty' && !companyTier) {
            setError('Company does not have a pricing tier configured. Set it in Master Data → Companies.');
            return;
        }
        onConfirm(viewType, null, []);
        handleClose();
    };

    const tierLabel = companyTier
        ? companyTier.charAt(0).toUpperCase() + companyTier.slice(1)
        : null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Invoice View</DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                            <FormLabel component="legend" sx={{ fontSize: 13, mb: 0.5 }}>
                                How should line items be generated?
                            </FormLabel>
                            <RadioGroup
                                value={viewType}
                                onChange={(e) => {
                                    setViewType(e.target.value);
                                    setError(null);
                                }}
                            >
                                <FormControlLabel value="price_qty" control={<Radio size="small" />} label="Customer Tier" />
                                <FormControlLabel value="component" control={<Radio size="small" />} label="Component" />
                            </RadioGroup>
                        </FormControl>

                        {viewType === 'price_qty' && (
                            <Box sx={{ mb: 1, px: 0.5 }}>
                                {tierLabel ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Rates will be auto-selected based on company tier:{' '}
                                        <strong>{tierLabel}</strong>
                                    </Typography>
                                ) : (
                                    <Alert severity="warning" sx={{ py: 0.5 }}>
                                        No tier set for this company. Configure it in Master Data → Companies.
                                    </Alert>
                                )}
                            </Box>
                        )}

                        {viewType === 'component' && (
                            <Box sx={{ mb: 1, px: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Components will be automatically derived from each sub-service order's component.
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleConfirm} variant="contained" disabled={loading}>
                    Generate Invoice
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InvoiceViewPreferencesDialog;
